import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import EquipmentProfileRepository from '@repository/EquipmentProfileRepository'

/*
 * Le catalogue de la politique de recharge (côté équipement), lu une fois par page : c'est lui qui dit quels types
 * d'équipement existent (les valeurs permises de `orders.kinds`) et lesquels ne sont pas encore
 * servis (permis mais absents du défaut, comme Linky). Aucune liste n'est recopiée ici.
 */
let cached = null

export function policyCatalogue() {
  if (!cached) {
    cached = CommissioningPolicyRepository.catalogue().catch(error => {
      // Lu sans le droit de gérer l'onboarding : l'écran fait sans, le serveur reste juge.
      cached = null
      throw error
    })
  }
  return cached
}

export const KINDS_KEY = 'orders.kinds'
export const PROFILES_KEY = 'profiles.member'
/** Le profil par sous-type (kind → { sous-type → profil }) ; `profiles.member` reste le profil « tous », en secours. */
export const PROFILES_BY_DEVICE_TYPE_KEY = 'profiles.member-by-device-type'
/** Ce qui porte une licence, donc un profil d'équipement (EquipmentKind::WITH_PROFILE côté serveur). */
export const PROFILE_KINDS = ['charging-point', 'meter-device', 'vehicle-meter', 'connected-vehicle']

/**
 * Ce qu'une politique donne à la personne (PolicyKey::orderableKinds côté serveur) : un type commandable et, pour ce
 * qui porte une licence, un profil d'équipement désigné — « tous les sous-types », ou au moins un par sous-type.
 */
export function kindsGivenBy(policySettings, allKinds) {
  const kinds = policySettings?.[KINDS_KEY]?.value || allKinds
  const profiles = policySettings?.[PROFILES_KEY]?.value || {}
  const byDeviceType = policySettings?.[PROFILES_BY_DEVICE_TYPE_KEY]?.value || {}
  return kinds.filter(kind => !PROFILE_KINDS.includes(kind) || !!profiles[kind] || Object.values(byDeviceType[kind] || {}).some(Boolean))
}

/**
 * Le profil qu'une politique désigne pour ce type et ce sous-type : celui du sous-type, sinon celui de « tous les
 * sous-types » (en secours).
 */
export function designatedProfileId(policySettings, kind, deviceType = null) {
  const byDeviceType = policySettings?.[PROFILES_BY_DEVICE_TYPE_KEY]?.value || {}
  return (deviceType && byDeviceType[kind]?.[deviceType]) || policySettings?.[PROFILES_KEY]?.value?.[kind] || null
}

let deviceTypesCached = null

/**
 * Les sous-types possibles par type d'équipement ({ 'charging-point': [...], 'meter-device': [...] }), lus une fois :
 * un type absent n'en a pas. Sans réponse du serveur, aucun sous-type (l'écran fait comme avant).
 */
export function deviceTypesByKind() {
  if (!deviceTypesCached) {
    deviceTypesCached = EquipmentProfileRepository.deviceTypes().catch(() => {
      deviceTypesCached = null
      return {}
    })
  }
  return deviceTypesCached
}

/** { all, notYetAvailable } : les types d'équipement, et ceux qu'on ne sait pas encore servir. */
export async function equipmentKinds() {
  const definition = (await policyCatalogue()).find(d => d.key === KINDS_KEY)
  const all = definition?.allowedValues || []
  const byDefault = definition?.default || []
  // Ce qu'on ne sait pas encore servir (Linky) n'apparaît sur aucun écran.
  return { all: all.filter(kind => byDefault.includes(kind)), notYetAvailable: all.filter(kind => !byDefault.includes(kind)) }
}

/**
 * Les équipements qu'une adhésion peut annoncer : ce que la politique de recharge donne à la personne, hors ce qui
 * n'est pas encore servi. Le serveur le vérifie à nouveau à la publication et à l'entrée.
 */
export async function orderableKinds(organizationId, policyId = null) {
  const [kinds, policy] = await Promise.all([
    equipmentKinds(),
    CommissioningPolicyRepository.get(organizationId, null, policyId).catch(() => null),
  ])
  return kindsGivenBy(policy?.settings, kinds.all).filter(kind => !kinds.notYetAvailable.includes(kind))
}
